/**
 * CelebrationListener — UI-5 / TD-V2-AUTO-21 + BUG-V2-042.
 *
 * Componente "fantasma" (renderiza só toast/overlay quando dispara) que
 * escuta novas notifications type='celebracao' via `useNotifications`
 * polling. Quando detecta uma celebração nova (não-dispatched antes),
 * dispara `fireConfetti()` + mostra overlay com título/mensagem.
 *
 * **BUG-V2-042 (2026-05-24):** filtro mudou de `!n.lida` pra
 * `!n.dispatchedAtIso`. Antes: a cada boot, confete redisparava enquanto
 * user não marcasse como lida (e user não tem motivo pra ler antes de
 * fechar overlay). Agora: backend marca `dispatched_at_iso` no primeiro
 * fire via `POST /:id/dispatched` (idempotente). Re-fire entre sessões
 * é impossível, mesmo se notif ficar não-lida.
 *
 * Persistência: `seenIds` ref previne re-fire DENTRO da mesma sessão
 * (React StrictMode + re-renders + polling 30s). `dispatched_at_iso` no
 * backend previne ENTRE sessões.
 *
 * Toggle de preferência: usa `isCelebracoesEnabled()` (localStorage de UX,
 * não dado de negócio).
 */

import { useEffect, useRef, useState } from 'react';

import { useNotifications, useNotificationActions } from '@/lib/notifications-store';
import { useAuth } from '@/contexts/AuthContext';
import { fireConfetti, isCelebracoesEnabled } from '@/lib/celebrations';

interface CelebracaoState {
  notifId: string;
  titulo: string;
  mensagem: string;
}

export function CelebrationListener() {
  const { user } = useAuth();
  // O técnico de campo (e-mail e senha, 25/10/2026) só abre o aplicativo do técnico: o servidor recusa as notificações
  // para ele, e consultá-las acendia o aviso vermelho "sem acesso" no celular. Para ele, este ouvinte não consulta nada.
  const userId = user && user.tipo !== 'tecnico' ? user.id : 'anonymous';
  const notifications = useNotifications(userId);
  const { markDispatched } = useNotificationActions(userId);

  const [active, setActive] = useState<CelebracaoState | null>(null);
  // Set de IDs já processados nesta sessão — previne re-fire em re-render
  // ou re-polling DENTRO da mesma sessão. Entre sessões, o backend
  // `dispatched_at_iso` é quem dedup (BUG-V2-042).
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    // BUG-V2-042: filtra por !dispatchedAtIso (não !lida). Backend marca
    // dispatched no primeiro fire, prevenindo re-trigger entre boots
    // mesmo se notif ficar não-lida.
    const candidatas = notifications.filter(
      (n) => n.type === 'celebracao' && !n.dispatchedAtIso && !seenIds.current.has(n.id)
    );
    if (candidatas.length === 0) return;
    candidatas.sort((a, b) =>
      a.criadoEmIso < b.criadoEmIso ? 1 : a.criadoEmIso > b.criadoEmIso ? -1 : 0
    );
    const target = candidatas[0];
    seenIds.current.add(target.id);

    const titulo =
      typeof target.payload.titulo === 'string' ? target.payload.titulo : '🎉 Celebração!';
    const mensagem = typeof target.payload.mensagem === 'string' ? target.payload.mensagem : '';

    if (isCelebracoesEnabled()) {
      fireConfetti();
      setActive({ notifId: target.id, titulo, mensagem });
      // Marca no backend imediatamente — confete tocou, próximo boot NÃO
      // deve re-tocar. Fire-and-forget: cache otimista do store já
      // atualiza local; falha de rede só causa re-fire no próximo boot
      // (aceitável, melhor que confete preso).
      void markDispatched(target.id);
      // Auto-dismiss após 6s (suficiente pra ler + apreciar)
      window.setTimeout(() => {
        setActive((prev) => (prev?.notifId === target.id ? null : prev));
      }, 6000);
    }
  }, [notifications, userId, markDispatched]);

  if (!active) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="celebration-overlay"
      className="pointer-events-auto fixed left-1/2 top-6 z-50 w-[90vw] max-w-sm -translate-x-1/2 rounded-xl border-2 border-red bg-paper-surface px-4 py-3 text-center shadow-xl dark:bg-paper-dark-surface"
    >
      <button
        type="button"
        onClick={() => setActive(null)}
        aria-label="Fechar"
        data-testid="celebration-close"
        className="absolute right-2 top-1 text-sm text-ink-3 hover:text-ink-1"
      >
        ✕
      </button>
      <p className="text-base font-semibold text-ink-1 dark:text-ink-dark-1">{active.titulo}</p>
      {active.mensagem && (
        <p className="mt-1 text-sm text-ink-2 dark:text-ink-dark-2">{active.mensagem}</p>
      )}
    </div>
  );
}
